import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import type { Pokemon, TeamResponse } from '../lib/types';
import { Button, EmptyState, Modal } from './ui';

const TEAM_SIZE = 6;

type FilterKey = 'all' | 'shiny' | 'favorite' | 'grove';

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'shiny', label: '✦ Shiny' },
  { key: 'favorite', label: '♥ Favourites' },
  { key: 'grove', label: 'In a Grove' },
];

/**
 * Choosing and ordering the six.
 *
 * The whole team is edited locally and saved in one request, matching the
 * server's single `PUT /team`. Add, remove and reorder are all just edits
 * to one array — three endpoints that each had to preserve the other two's
 * ordering invariants is exactly where this kind of feature grows bugs.
 */
export function TeamEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: Pokemon[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push } = useToast();
  const [team, setTeam] = useState<Pokemon[]>(initial);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [busy, setBusy] = useState(false);
  /** Index being dragged, and the index it is currently hovering over. */
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  /** Narrow the pool without retyping a search every time. */
  const [filter, setFilter] = useState<FilterKey>('all');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const choices = useAsync<{ pokemon: Pokemon[] }>(
    () => api.get('/team/choices', { search: debounced || undefined }),
    [debounced],
  );

  const inTeam = new Set(team.map((pokemon) => pokemon.id));

  const visible = (choices.data?.pokemon ?? []).filter((pokemon) => {
    if (filter === 'shiny') return pokemon.isShiny;
    if (filter === 'favorite') return pokemon.isFavorite;
    if (filter === 'grove') return pokemon.inGrove;
    return true;
  });

  function toggle(pokemon: Pokemon) {
    setTeam((current) => {
      if (current.some((row) => row.id === pokemon.id)) {
        return current.filter((row) => row.id !== pokemon.id);
      }
      if (current.length >= TEAM_SIZE) {
        push('info', `A Team holds ${TEAM_SIZE}. Remove one first.`);
        return current;
      }
      return [...current, pokemon];
    });
  }

  function shift(index: number, direction: -1 | 1) {
    move(index, index + direction);
  }

  /**
   * Moves a slot, by drag or by arrow.
   *
   * Both gestures land here so there is one definition of what reordering *is*.
   * An earlier draft had the arrows swap neighbours and the drag splice, which
   * meant dragging three places produced a different arrangement from pressing
   * an arrow three times.
   */
  function move(from: number, to: number) {
    if (to < 0 || to >= team.length || from === to) return;
    setTeam((current) => {
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    try {
      await api.put<TeamResponse>('/team', {
        pokemonIds: team.map((pokemon) => pokemon.id),
      });
      push('success', 'Team updated.', '★');
      onSaved();
      onClose();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save your team.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} labelledBy="team-editor-title" wide>
      <div className="stack">
        <div className="row row--between">
          <h2 id="team-editor-title">My Team</h2>
          <span className="chip">
            {team.length} / {TEAM_SIZE}
          </span>
        </div>

        <p className="tiny muted" style={{ margin: 0 }}>
          The six travelling with you. They appear on your profile, visitors can feed them, and they
          gain EXP whenever you help another trainer. Nothing to do with your Groves — a Team member
          can live anywhere, or nowhere. Fewer than six is fine.
        </p>

        {/* The Team, in order. Position matters: slot 1 is the Lead Pokémon,
            shown on trainer cards, player popovers and guild rosters.

            Drag to reorder, *and* arrows. Not one or the other: a drag surface
            alone is unusable with a keyboard and awkward on a phone, and arrows
            alone make moving slot 6 to slot 1 five presses. Both gestures call
            the same `move`, so they cannot disagree about what reordering
            means. */}
        <div className="team-editor__team">
          {team.length === 0 ? (
            <div className="pxwell tiny muted" style={{ padding: 'var(--gap-sm)', width: '100%' }}>
              Pick some Pokémon below to build your Team.
            </div>
          ) : (
            team.map((pokemon, index) => (
              <div
                key={pokemon.id}
                className={`pxwell team-editor__slot ${
                  dragging === index ? 'team-editor__slot--dragging' : ''
                } ${
                  over === index && dragging !== null && dragging !== index
                    ? 'team-editor__slot--over'
                    : ''
                }`}
                draggable
                onDragStart={(event) => {
                  setDragging(index);
                  // Firefox refuses to start a drag with no payload.
                  event.dataTransfer.setData('text/plain', pokemon.id);
                  event.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setOver(index);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (dragging !== null) move(dragging, index);
                  setDragging(null);
                  setOver(null);
                }}
                onDragEnd={() => {
                  setDragging(null);
                  setOver(null);
                }}
              >
                <span className="team-editor__rank mono tiny">
                  {index === 0 ? 'Lead' : index + 1}
                </span>
                <img className="pixel" src={pokemon.sprite} alt="" />
                <span className="tray-item__name">{pokemon.displayName}</span>
                <span className="tiny muted">Lv {pokemon.level}</span>
                <div className="row" style={{ gap: 2 }}>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={index === 0}
                    onClick={() => shift(index, -1)}
                    aria-label={`Move ${pokemon.displayName} earlier`}
                  >
                    ◀
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={index === team.length - 1}
                    onClick={() => shift(index, 1)}
                    aria-label={`Move ${pokemon.displayName} later`}
                  >
                    ▶
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => toggle(pokemon)}
                    aria-label={`Remove ${pokemon.displayName} from your Team`}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <hr className="divider" />

        <input
          className="input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search your collection"
        />

        {/* Filters run on the page the server already sent. The endpoint
            returns 120 rows ordered favourites-then-shiny-then-level, so these
            narrow what is on screen rather than asking again — pressing
            "Shiny" on a slow connection should not be a round trip. */}
        <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
          {FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`chip ${filter === option.key ? 'chip--on' : ''}`}
              onClick={() => setFilter(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {choices.loading ? (
          <div className="pxwell skeleton" style={{ height: 220 }} />
        ) : visible.length > 0 ? (
          <div className="grove-tray">
            {visible.map((pokemon) => (
              <button
                key={pokemon.id}
                type="button"
                className={`pxframe tray-item ${pokemon.isShiny ? 'pkmn-card--shiny' : ''} ${
                  inTeam.has(pokemon.id) ? 'tray-item--chosen' : ''
                }`}
                onClick={() => toggle(pokemon)}
                aria-pressed={inTeam.has(pokemon.id)}
              >
                <img className="pixel" src={pokemon.sprite} alt="" loading="lazy" />
                <span className="tray-item__name">{pokemon.displayName}</span>
                <span className="tiny muted">
                  Lv {pokemon.level}
                  {pokemon.isShiny ? ' ✦' : ''}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <EmptyState art="◓" title="Nothing found">
            Hatch an egg to start your collection.
          </EmptyState>
        )}

        <div className="row">
          <Button variant="go" loading={busy} disabled={busy} onClick={() => void save()} style={{ flex: 1 }}>
            Save Team
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Modal>
  );
}
